/**
 * Panel — the reference's `data-screen="dashboard"`.
 *
 * Ported against the prototype's rendered DOM (`Screens.dashboard`, `m08-admin.js`): the page
 * head with its eyebrow and two actions, three `.kpi` cards, and a `.split` holding the recent
 * uploads table and the «Dikkat isteyenler» list. The attention rows are conditional in the
 * reference and are conditional here, in the same order, so a seed with no bounced e-mail shows
 * the same list the reference would show for that seed.
 */
import type { ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdoptBatches, useAdoptTemplateCounts } from '../data/adopt';
import { useV07Queries } from '../data/queries-context';
import { useDemoData } from '../data/store';
import { useRead } from '../data/use-read';
import {
  batchSummary,
  displayName,
  fmtDate,
  fmtDateTime,
  fmtNum,
  parseISO,
  publishedTemplates,
  TR_MONTHS,
  todayIso,
  viewDateIsPinned,
} from '../lib/reference';

/**
 * The server's own `batch_status`, in the product's words.
 *
 * The two branches the table has always had — `completed` and `completed_with_errors` — are the
 * demo's statuses, and the only ones the frozen prototype ever produced. A real deployment stores
 * `draft | planned | committed | failed` (migration 001), none of which they match, so every real
 * upload fell through to a branch that printed the raw database value and the Panel showed an
 * administrator «failed», «committed» and «planned». The approved reference names them; the
 * colours it gives them are measured to be the product's own `--red-soft`, `--yellow-soft` and
 * `--surface-3`, so nothing here is a new palette. A value this table does not know still prints
 * itself rather than disappearing.
 */
export const BATCH_STATUS: Record<string, { label: string; tone: string }> = {
  committed: { label: 'Tamamlandı', tone: 'pill-info' },
  failed: { label: 'Başarısız', tone: 'pill-danger' },
  planned: { label: 'Planlandı', tone: 'pill-neutral' },
  draft: { label: 'Taslak', tone: 'pill-neutral' },
};

function Kpi({ label, value, sub }: { label: string; value: number; sub: string }): ReactElement {
  return (
    <div className="kpi">
      <span className="eyebrow">{label}</span>
      <div className="value">{fmtNum(value)}</div>
      <div className="sub">{sub}</div>
    </div>
  );
}

/**
 * The «Bu ay» caption.
 *
 * The reference prints the month it counted plus «(prototip tarihi 02.09.2026)», and it has to:
 * its numbers are relative to a frozen day and a reviewer comparing the demo to the reference
 * deserves to be told which day that is. The product counts the month it is actually in, so the
 * parenthesis has nothing left to explain — and leaving it there would have told an administrator
 * that their console was reporting a date in the past. The clock itself decides which caption
 * applies, so the demo keeps the reference's wording to the character.
 */
function monthSub(today: Date | null): string {
  const month = today ? TR_MONTHS[today.getMonth()] : '';
  const year = today ? today.getFullYear() : '';
  const counted = `${month} ${year} içinde oluşturulan`;
  return viewDateIsPinned() ? `${counted} (prototip tarihi ${fmtDate(todayIso(), 'tr', 'short')})` : counted;
}

export function DashboardScreen(): ReactElement {
  const db = useDemoData();
  const queries = useV07Queries();
  const navigate = useNavigate();
  const today = parseISO(todayIso());
  const drafts = db.templates.filter((t) => t.status === 'draft').length;
  const archived = db.templates.filter((t) => t.status === 'archived').length;

  /*
   * The totals are counted where the records are.
   *
   * They used to be `dashboardStats(db)` — the store, which held one page of the register — so a
   * deployment with three hundred credentials reported two hundred, the month's count was the
   * month's count WITHIN that page, and «önümüzdeki 30 gün» measured a horizon against a fifth of
   * the data. None of that was visible as an error: the number was simply wrong and looked fine.
   */
  const summaryRead = useRead('dashboard:summary', () => queries.registerSummary());
  const counted = summaryRead.value;
  /*
   * The same field names the reference's own `dashboardStats` produced, so the KPI strings below are
   * untouched. Templates and releases are still the store's: they are loaded whole, because a
   * credential renders from its own release.
   */
  const S = {
    st: {
      draft: counted?.byState.draft ?? 0,
      pending_acceptance: counted?.byState.pending_acceptance ?? 0,
      pending_admin_publish: counted?.byState.pending_admin_publish ?? 0,
      active: counted?.byState.active ?? 0,
      expired: counted?.byState.expired ?? 0,
      revoked: counted?.byState.revoked ?? 0,
      superseded: counted?.byState.superseded ?? 0,
    },
    total: counted?.total ?? 0,
    thisMonth: counted?.issuedThisMonth ?? 0,
    bounced: counted?.bounced ?? 0,
    queued: counted?.queued ?? 0,
    templates: db.templates.length,
    published: publishedTemplates(db).length,
  };
  /* Six most recent uploads — a page of six, not every batch and every row of every batch. */
  const recentRead = useRead('dashboard:batches', () => queries.batchPage({ page: 1, perPage: 6 }));
  const recent = recentRead.value?.rows ?? [];
  useAdoptBatches(recentRead.value?.rows ?? null);
  useAdoptTemplateCounts(counted?.byTemplate ?? null);

  /* The reference's attention list, in its own order and with its own conditions. */
  const rows: ReactElement[] = [];
  if (S.bounced)
    rows.push(
      <div className="row" key="bounced">
        <span className="sq" />
        <span>
          <b>{`${S.bounced} e-posta geri döndü`}</b>
          {' — adresleri düzeltip onay e-postasını yeniden gönderin.'}
        </span>
        <Link className="btn btn-ghost btn-sm" to="/admin/credentials?delivery=bounced">
          Geri dönen e-postayı düzelt
        </Link>
      </div>,
    );
  if (S.st.pending_acceptance)
    rows.push(
      <div className="row" key="pending">
        <span className="sq y" />
        <span>
          <b>{`${S.st.pending_acceptance} kayıt alıcı onayı bekliyor`}</b>
          {' — onaylanana kadar public sayfa kişisel veri göstermez.'}
        </span>
        <Link className="btn btn-ghost btn-sm" to="/admin/credentials?status=pending_acceptance">
          Onay bekleyenleri listele
        </Link>
      </div>,
    );
  for (const c of db.credentials) {
    const report = (c as { issueReport?: { message?: string } }).issueReport;
    if (!report) continue;
    rows.push(
      <div className="row" key={`report-${c.id}`}>
        <span className="sq" />
        <span>
          <b>{displayName(c)}</b>
          {` hata bildirdi: «${report.message}»`}
        </span>
        <Link className="btn btn-ghost btn-sm" to={`/admin/credentials/${c.publicCode}`}>
          Düzelt
        </Link>
      </div>,
    );
  }
  for (const b of db.batches.filter((x) => x.status === 'completed_with_errors')) {
    const s = batchSummary(b, db);
    rows.push(
      <div className="row" key={`batch-${b.id}`}>
        <span className="sq y" />
        <span>
          <b>{b.filename}</b>
          {` — ${s.invalid} satır hatalı kaldı, ${s.created} kayıt oluşturuldu.`}
        </span>
        <Link className="btn btn-ghost btn-sm" to={`/admin/import?batch=${b.id}`}>
          Batch
        </Link>
      </div>,
    );
  }
  if (S.queued)
    rows.push(
      <div className="row" key="queued">
        <span className="sq y" />
        <span>
          <b>{`${S.queued} e-posta kuyrukta`}</b>
          {' — Resend teslim raporu bekleniyor.'}
        </span>
        <Link className="btn btn-ghost btn-sm" to="/admin/credentials?delivery=pending">
          Gör
        </Link>
      </div>,
    );
  /* Counted by the server, over every record — never over the page the browser happens to hold. */
  const expiringSoon = counted?.expiringSoon ?? 0;
  if (expiringSoon)
    rows.push(
      <div className="row" key="expiring">
        <span className="sq y" />
        <span>
          <b>{`${expiringSoon} kayıt`}</b>
          {' önümüzdeki 30 gün içinde sona eriyor.'}
        </span>
        <Link className="btn btn-ghost btn-sm" to="/admin/credentials?status=active">
          Gör
        </Link>
      </div>,
    );

  /*
   * Zero is an answer, and «I could not count» is not.
   *
   * A panel of noughts is indistinguishable from an empty deployment, so when the totals could not be
   * read the screen says so above them instead of printing them as fact.
   */
  const totalsNotice =
    summaryRead.status === 'loading' ? (
      <p className="muted" aria-busy="true">
        Toplamlar yükleniyor…
      </p>
    ) : summaryRead.status === 'error' ? (
      <div className="warn-box" role="alert">
        <b>
          {summaryRead.error?.kind === 'session'
            ? 'Oturum sona erdi — toplamlar okunamadı.'
            : summaryRead.error?.kind === 'forbidden'
              ? 'Toplamları görme yetkiniz yok.'
              : summaryRead.stale
                ? 'Toplamlar yenilenemedi; aşağıdaki sayılar güncel değil.'
                : 'Toplamlar okunamadı.'}
        </b>{' '}
        {summaryRead.error?.kind === 'session' ? null : (
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            id="kpiRetry"
            onClick={summaryRead.retry}
          >
            Yeniden dene
          </button>
        )}
      </div>
    ) : null;

  return (
    <section data-screen="dashboard">
      <div className="page-head">
        <div>
          <span className="eyebrow">Happy Place to Work · Yönetim</span>
          <h1>Panel</h1>
          <p className="lead">Şablonlar, üretilen credential'lar ve son toplu yüklemeler tek bakışta.</p>
        </div>
        <div className="page-actions">
          <Link className="btn btn-secondary" to="/admin/templates?new=1">
            + Yeni Şablon
          </Link>
          <Link className="btn btn-primary" to="/admin/import">
            Toplu Üretim
          </Link>
        </div>
      </div>
      <div className="stack">
        {totalsNotice}
        <div className="kpis" id="kpis">
          <Kpi
            label="Şablonlar"
            value={S.templates}
            sub={`${S.published} yayında · ${drafts} taslak · ${archived} arşiv · sınır yok`}
          />
          <Kpi
            label="Credential'lar"
            value={S.total}
            sub={`${fmtNum(S.st.active)} aktif · ${S.st.pending_acceptance} onay bekliyor · ${S.st.expired} süresi dolmuş · ${S.st.revoked} iptal`}
          />
          <Kpi label="Bu ay" value={S.thisMonth} sub={monthSub(today)} />
        </div>
        <div className="split">
          <div className="card">
            <div className="card-head">
              <h2>Son yüklemeler</h2>
              <Link className="btn btn-ghost btn-sm" to="/admin/import">
                Yeni yükleme
              </Link>
            </div>
            <div className="table-wrap">
              <table className="table" id="recentImports">
                <thead>
                  <tr>
                    <th>Dosya</th>
                    <th>Şablon</th>
                    <th className="num">Satır</th>
                    <th>Sonuç</th>
                    <th>Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((b) => {
                    const t = db.templates.find((x) => x.id === b.templateId);
                    const s = batchSummary(b, db);
                    const known = BATCH_STATUS[b.status];
                    return (
                      <tr
                        className="is-link"
                        data-batch={b.id}
                        key={b.id}
                        onClick={() => navigate(`/admin/import?batch=${b.id}`)}
                      >
                        <td>
                          <b style={{ fontWeight: 500 }}>{b.filename}</b>
                          <br />
                          <span className="small muted">
                            {`${b.createdBy}${s.pending ? ` · ${s.pending} onay bekliyor` : ''}`}
                          </span>
                        </td>
                        <td>
                          <span className="pill pill-neutral sq">{t ? t.code : b.templateId}</span>
                        </td>
                        <td className="num tnum">{`${s.created} / ${s.total}`}</td>
                        <td>
                          {b.status === 'completed' ? (
                            <span className="pill pill-active">✓ Tamamlandı</span>
                          ) : b.status === 'completed_with_errors' ? (
                            <span className="pill pill-danger">{`⚠ ${s.invalid} hatalı satır`}</span>
                          ) : known ? (
                            <span className={`pill ${known.tone}`}>{known.label}</span>
                          ) : (
                            <span className="pill pill-info">{b.status}</span>
                          )}
                        </td>
                        <td className="small muted nowrap">{fmtDateTime(b.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card">
            <div className="card-head">
              <h2>Dikkat isteyenler</h2>
            </div>
            <div className="card-pad attn" id="attention">
              {rows.length > 0 ? rows : <p className="muted">Dikkat isteyen bir konu yok.</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
